<template>
  <div class="min-h-screen bg-slate-50 py-10 px-4">
    <div class="mx-auto max-w-6xl grid gap-6 lg:grid-cols-[240px,1fr]">
      <aside class="sticky top-4 self-start rounded-2xl border border-slate-100 bg-white p-4 shadow-sm shadow-slate-200/60 space-y-3">
        <h2 class="text-sm font-semibold text-slate-800">Profile sections</h2>
        <ul class="space-y-1">
          <li v-for="section in navSections" :key="section.id">
            <button
              class="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              :class="section.complete ? 'text-slate-700' : 'text-slate-500'"
              @click="scrollToAnchor(section.anchor)"
            >
              <span class="flex items-center gap-2">
                <span class="inline-flex h-2.5 w-2.5 rounded-full" :class="section.complete ? 'bg-emerald-500' : 'bg-slate-300'"></span>
                {{ section.label }}
              </span>
              <span class="text-xs uppercase tracking-wide text-slate-400">{{ section.hint }}</span>
            </button>
          </li>
        </ul>
      </aside>

      <div class="space-y-6">
        <!-- Profile visibility -->
        <section id="sec-visibility" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Profile visibility</h2>
              <p class="text-sm text-slate-500">Choose whether companies on Screened can find you. You can change this at any time.</p>
            </div>
            <span class="shrink-0 pt-1 text-xs text-slate-400">{{ visibilitySaving ? 'Saving…' : 'Saves automatically' }}</span>
          </div>
          <ProfileVisibilityPicker
            name="profile-visibility"
            :model-value="profile.profile_visibility || 'public'"
            :disabled="visibilitySaving"
            @update:model-value="changeProfileVisibility"
          />
        </section>

        <!-- Resume -->
        <section id="sec-resume" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50">
          <div class="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Resume</h2>
              <p class="text-sm text-slate-500">Keep your latest resume on file.</p>
            </div>
          </div>
          <div v-if="uploadData" class="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div v-if="uploadData.replacement" class="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p class="text-sm font-semibold text-amber-800">
                A new resume ({{ uploadData.replacement.file_name }}) has been uploaded and is being processed.
              </p>
              <p class="mt-1 text-xs text-amber-700">
                This may take a few minutes. Once completed, your profile will be updated, and you'll be able to upload another resume.
              </p>
            </div>
            <div v-else class="space-y-3">
              <div v-if="replaceFile" class="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p class="text-sm font-semibold text-amber-800">
                  You are about to replace your current resume with {{ replaceFile.name }}
                </p>
                <p class="mt-1 text-xs text-amber-700">
                  It may take several minutes for your information to update. During this time, you won't be able to upload another resume.
                </p>
                <div class="mt-4 flex gap-3">
                  <button @click="replaceNotificationConfirm" class="inline-flex items-center justify-center rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60">
                    Confirm
                  </button>
                  <button @click="replaceNotificationCancel" class="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-200">
                    Cancel
                  </button>
                </div>
              </div>

              <div class="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
                <div class="space-y-1">
                  <span class="text-base font-semibold text-slate-900">{{ uploadData.active.file_name }}</span>
                  <div class="text-xs text-slate-500">Added on {{ uploadData.active.formatted_upload_date }}</div>
                </div>
                <div v-if="!uploadData.replacement" class="flex gap-3">
                  <button @click="downloadResume" class="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-200">
                    Download
                  </button>
                  <button @click="replaceResumeClick" class="inline-flex items-center justify-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                    Replace
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div class="flex items-center justify-between">
              <div class="flex flex-col space-y-2">
                <ContentLoader :speed="2" :width="250" :height="55">
                  <rect x="0" y="0" rx="3" ry="3" width="220" height="16" />
                  <rect x="0" y="24" rx="3" ry="3" width="160" height="16" />
                </ContentLoader>
              </div>
              <div class="flex gap-3">
                <ContentLoader :speed="2" :width="200" :height="50">
                  <rect x="0" y="0" rx="3" ry="3" width="90" height="40" />
                  <rect x="110" y="0" rx="3" ry="3" width="90" height="40" />
                </ContentLoader>
              </div>
            </div>
          </div>
        </section>

        <!-- Personal -->
        <section id="sec-personal" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-6">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Personal details</h2>
              <p class="text-sm text-slate-500">How recruiters can reach you.</p>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-firstName">First name</label>
              <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-firstName" type="text" v-model="profile.first_name">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-lastName">Last name</label>
              <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-lastName" type="text" v-model="profile.last_name">
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-phone">Phone</label>
            <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-Phone" type="text" v-model="profile.phone">
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-address">Street Address</label>
              <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-address" type="text" v-model="profile.address">
            </div>
            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-suburb">Suburb</label>
                <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-suburb" type="text" v-model="profile.suburb">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-state">State</label>
                <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-state" type="text" v-model="profile.state">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-postCode">Post Code</label>
                <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-postCode" type="text" v-model="profile.post_code">
              </div>
            </div>
          </div>
        </section>

        <!-- Visa -->
        <section id="sec-visa" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-900">Visa status</h2>
            <p class="text-sm text-slate-500">Tell us your work rights.</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-visaStatus">Visa status</label>
            <select class="block w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-visaStatus" v-model="profile.visa_status">
              <option value="Citizen">Citizen</option>
              <option value="Permanent Resident">Permanent Resident</option>
              <option value="Visa with full working rights">Visa with full working rights</option>
              <option value="Visa that requires sponsorship">Visa that requires sponsorship</option>
              <option value="Working Holiday Visa">Working Holiday Visa</option>
              <option value="I’m not a citizen and don’t hold a visa">I’m not a citizen and don’t hold a visa</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div v-if="profile?.visa_status == 'Other'">
            <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-visa-other">Please specify your visa status</label>
            <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-visa-other" type="text" v-model="profile.visa_status_other">
          </div>
        </section>

        <!-- Role & questions -->
        <section id="sec-role" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-6">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Industry & role</h2>
              <p class="text-sm text-slate-500">Choose where you fit best.</p>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2" v-if="industries.length > 0">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-industry">Industry</label>
              <select @change="changeIndustry()" class="block w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-industry" v-model="selectedIndustry">
                <option disabled value="">Select an option</option>
                <option v-for="industry in industries" :value="industry.id">{{ industry.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-occupation">Role</label>
              <select class="block w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-occupation" v-model="selectedRole">
                <option disabled value="">Select an option</option>
                <template v-if="hasRoleCategories">
                  <optgroup
                    v-for="group in groupedRoles"
                    :key="group.name"
                    :label="group.name"
                  >
                    <option v-for="role in group.roles" :key="role.id" :value="role.id">{{ role.name }}</option>
                  </optgroup>
                </template>
                <template v-else>
                  <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
                </template>
              </select>
            </div>
          </div>

          <div v-if="questions.length > 0" class="space-y-4">
            <p class="text-sm font-semibold text-slate-800">Role specific questions</p>
            <div
              v-for="(question, index) in questions"
              :key="question.id"
              class="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <label :for="'question-' + question.id" class="block text-sm font-medium text-slate-800">{{ question.question }}</label>
              <textarea
                class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand"
                :id="'question-' + question.id"
                rows="3"
                v-model="answers[index]"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- Technical expertise (only for role categories that define options) -->
        <section v-if="expertiseGroups.length > 0" id="sec-expertise" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-6">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Technical expertise</h2>
              <p class="text-sm text-slate-500">Select the areas where you have hands-on experience.</p>
            </div>
            <span class="shrink-0 pt-1 text-xs uppercase tracking-wide text-slate-400">Optional</span>
          </div>

          <fieldset v-for="group in expertiseGroups" :key="group.id" class="space-y-3">
            <legend class="mb-3 text-sm font-semibold text-slate-800">{{ group.name }}</legend>
            <div class="grid gap-2 sm:grid-cols-2">
              <label
                v-for="option in group.options"
                :key="option.id"
                class="flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
                :class="selectedExpertise.includes(option.id) ? 'border-brand' : 'border-slate-200'"
              >
                <input
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand focus:ring-brand"
                  :value="option.id"
                  v-model="selectedExpertise"
                >
                <span>{{ option.name }}</span>
              </label>
            </div>
          </fieldset>
        </section>

        <!-- Career highlights -->
        <section id="sec-highlights" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Career highlights</h2>
              <p class="text-sm text-slate-500">Share some key achievements, projects or contributions that demonstrate the value you’ve delivered.</p>
            </div>
            <span class="shrink-0 pt-1 text-xs uppercase tracking-wide text-slate-400">Optional</span>
          </div>

          <div v-for="(highlight, index) in careerHighlights" :key="index" class="space-y-1">
            <label :for="'highlight-' + index" class="block text-sm font-medium text-slate-700">Highlight {{ index + 1 }}</label>
            <textarea
              :id="'highlight-' + index"
              class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand focus:ring-1 focus:ring-brand"
              rows="3"
              :maxlength="careerHighlightMaxLength"
              :placeholder="index === 0 ? 'e.g. Led a 15,000m drilling programme that contributed to an increase in the project’s mineral resource.' : ''"
              v-model="careerHighlights[index]"
            ></textarea>
            <div class="flex items-center justify-between text-xs">
              <button
                v-if="index >= minCareerHighlights"
                type="button"
                class="font-medium text-rose-500 hover:text-rose-600"
                @click="removeCareerHighlight(index)"
              >
                Remove
              </button>
              <span v-else></span>
              <span :class="highlight.length >= careerHighlightMaxLength ? 'text-amber-600' : 'text-slate-400'">
                {{ highlight.length }}/{{ careerHighlightMaxLength }}
              </span>
            </div>
          </div>

          <button
            v-if="careerHighlights.length < maxCareerHighlights"
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
            @click="addCareerHighlight"
          >
            + Add another highlight
          </button>
        </section>

        <!-- Remuneration & availability -->
        <section id="sec-remuneration" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-6">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Target remuneration</h2>
              <p class="text-sm text-slate-500">Provide salary and/or contract preferences.</p>
            </div>
          </div>

          <div>
            <span class="block text-sm font-medium text-slate-700 mb-1">Salary (Annual) <span class="ml-2 text-xs text-slate-400 uppercase">Excluding super</span></span>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500 mb-1" for="grid-salary-min">Minimum</label>
                <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-salary-min" type="number" min="0" step="1" placeholder="e.g. 150000" v-model="profile.salary_min">
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1" for="grid-salary-max">Maximum</label>
                <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-salary-max" type="number" min="0" step="1" placeholder="e.g. 170000" v-model="profile.salary_max">
              </div>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <span class="block text-sm font-medium text-slate-700 mb-1">Rate <span class="ml-2 text-xs text-slate-400 uppercase">Excluding super</span></span>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-slate-500 mb-1" for="grid-rate-min">Minimum</label>
                  <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-rate-min" type="number" min="0" step="1" placeholder="e.g. 850" v-model="profile.rate_min">
                </div>
                <div>
                  <label class="block text-xs text-slate-500 mb-1" for="grid-rate-max">Maximum</label>
                  <input class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-rate-max" type="number" min="0" step="1" placeholder="e.g. 950" v-model="profile.rate_max">
                </div>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-rateType">Rate type</label>
              <div class="hidden md:block h-5" aria-hidden="true"></div>
              <select class="block w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-rateType" v-model="profile.rate_type">
                <option disabled value="">Select an option</option>
                <option value="hour">Hourly</option>
                <option value="day">Daily</option>
                <option value="month">Monthly</option>
              </select>
            </div>
          </div>

            <div v-if="showContractEngagementType" class="space-y-3">
            <label class="block text-sm font-medium text-slate-700">Contract Engagement Type <span class="ml-2 text-xs text-slate-400 uppercase">Please select an option</span></label>
            <div class="grid gap-3 md:grid-cols-2">
              <label
                v-for="option in contractStatusOptions"
                :key="option.value"
                class="flex items-center justify-between gap-3 rounded-full border px-4 py-3 text-sm font-medium text-gray-700 transition cursor-pointer"
                :class="{ 'border-brand text-brand shadow-sm': profile.contract_type === option.value }"
              >
                <input type="radio" class="sr-only" name="contract-status" :value="option.value" v-model="profile.contract_type">
                <span class="mx-2">{{ option.label }}</span>
                <span class="inline-block h-4 w-4 rounded-full border-2 transition"
                  :class="profile.contract_type === option.value ? 'border-brand bg-brand' : 'border-gray-300 bg-white'"></span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1" for="grid-availability">Availability</label>
            <div class="relative">
              <select @change="availabilityChange($event)" class="block w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-availability" v-model="selectedAvailability">
                <option disabled value="">Select an option</option>
                <option v-for="availability in availabilityOptions" :value="availability.value">{{ availability.label }}</option>
              </select>
            </div>
            <Popover v-if="calendarOpen" class="relative">
              <PopoverPanel class="absolute z-10" static>
                <Datepicker class="my-3" v-model="date" :min-date="today" inline autoApply @update:modelValue="updateCalendarDate" hideOffsetDates :enableTimePicker="false" ref="datepicker"></Datepicker>
              </PopoverPanel>
            </Popover>
          </div>
        </section>

        <!-- Photo -->
        <section id="sec-photo" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">Profile photo</h2>
              <p class="text-sm text-slate-500">A clear, recent portrait helps your profile stand out.</p>
            </div>
            <button
              v-if="profilePicturePreview"
              type="button"
              class="text-sm font-medium text-rose-500 hover:text-rose-600"
              @click="removePhoto"
            >
              Remove
            </button>
          </div>

          <div class="flex flex-col gap-4 md:flex-row md:items-center">
            <div class="flex items-center gap-4">
              <div class="h-24 w-24 overflow-hidden rounded-full border border-slate-200 bg-slate-100">
                <img v-if="profilePicturePreview" :src="profilePicturePreview" alt="Profile preview" class="h-full w-full object-cover" />
                <div v-else class="flex h-full w-full items-center justify-center text-xs text-slate-400">No photo</div>
              </div>
              <div class="space-y-2 text-sm text-slate-600">
                <p class="font-medium text-slate-800">Upload or adjust your photo</p>
                <p class="text-xs text-slate-500">JPG or PNG up to 25MB. We compress before saving.</p>
                <p v-if="isCompressingPhoto" class="flex items-center gap-2 text-xs font-medium text-slate-500">
                  <svg class="h-4 w-4 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"></path>
                  </svg>
                  Optimizing your photo…
                </p>
              </div>
            </div>
            <div class="flex flex-wrap gap-3">
              <label class="inline-flex cursor-pointer items-center justify-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                Upload photo
                <input type="file" accept="image/*" class="hidden" @change="handleProfilePictureUpload" />
              </label>
              <button
                v-if="profilePicturePreview"
                type="button"
                class="text-sm font-medium text-brand hover:text-brand/80"
                @click="reCropPhoto"
              >
                Adjust crop
              </button>
            </div>
          </div>
        </section>

        <!-- Summary -->
        <section id="sec-summary" class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50 space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-900">Summary</h2>
            <p class="text-sm text-slate-500">Short description of preferred role/project</p>
          </div>
          <textarea class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" id="grid-roleDescription" rows="3" v-model="profile.ideal_role"></textarea>
        </section>

        <div class="flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-sm shadow-slate-200/50">
          <router-link to="/contractor/profile">
            <button class="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-200" type="button">
              Back
            </button>
          </router-link>
          <button class="inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition hover:bg-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40" type="button" @click="submitUpdate">
            Save changes
          </button>
        </div>
      </div>
    </div>

    <input type="file" ref="fileInput" class="hidden" @change="handleResumeReplacementUpload"  accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document" />

    <ImageCropperModal
        v-if="showModal && profilePicturePreview"
        :image="profilePicturePreview"
        :show="showModal"
        @close="closeModal"
        @save="saveImage"
    />
  </div>
</template>

<script>
let apiEndpoint = process.env.VUE_APP_API_BASEURL
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import ImageCropperModal from '@/components/modals/ImageCropperModal.vue';
import ProfileVisibilityPicker from '@/components/marketplace/contractor/ProfileVisibilityPicker.vue';
import contractorService from '@/services/marketplace/contractor.service';
import genericService from '../../../services/marketplace/generic.service';
import { ALLOWED_RESUME_EXTENSIONS, ALLOWED_RESUME_MIME_TYPES } from "@/constants";
import { Popover, PopoverPanel } from "@headlessui/vue";
import { ContentLoader } from 'vue-content-loader'
import imageCompression from 'browser-image-compression';
  
export default {
  name: 'MarketplaceContractorProfileEdit',
  inject: ['toggleLoading'],
  components: {
    Datepicker,
    ImageCropperModal,
    ProfileVisibilityPicker,
    Popover,
    PopoverPanel,
    ContentLoader
  },
  data() {
    return {
      date: new Date(),
      today: new Date(),
      profile: [],
      availability: 0,
      calendarOpen: false,
      navSectionsBase: [
        { id: 'visibility', label: 'Visibility', anchor: '#sec-visibility', hint: 'Public/Private' },
        { id: 'resume', label: 'Resume', anchor: '#sec-resume', hint: 'Upload' },
        { id: 'personal', label: 'Personal', anchor: '#sec-personal', hint: 'Contact' },
        { id: 'visa', label: 'Visa', anchor: '#sec-visa', hint: 'Status' },
        { id: 'role', label: 'Industry & Role', anchor: '#sec-role', hint: 'Select' },
        { id: 'expertise', label: 'Expertise', anchor: '#sec-expertise', hint: 'Optional' },
        { id: 'highlights', label: 'Highlights', anchor: '#sec-highlights', hint: 'Optional' },
        { id: 'remuneration', label: 'Remuneration', anchor: '#sec-remuneration', hint: 'Salary/Rate' },
        { id: 'photo', label: 'Photo', anchor: '#sec-photo', hint: 'Optional' },
        { id: 'summary', label: 'Summary', anchor: '#sec-summary', hint: 'Describe' },
      ],
      availabilityOptions: [
        { "value": 0.1, "label": "Now"},
        { "value": 0.2, "label": "1 week" },
        { "value": 0.5, "label": "2 weeks" },
        { "value": 1, "label": "1 month" },
        { "value": 999, "label": "Custom date" }
      ],
      profilePicturePreview: null,
      showModal: false,
      profilePictureName: '',
      activePreviewUrl: null,
      isCompressingPhoto: false,
      hasPhotoLoaded: false,
      selectedAvailability: 0,
      resumeData: false,
      editResumeData: false,
      isFileUploaded: false,
      uploadedFile: null,
      fileSuccess: false,
      fileError: false,
      fileErrorMessage: '',
      formData: {
        profilePicture: null,
        croppedImage: null
      },
      industries: [],
      selectedIndustry: '',
      roles: [],
      selectedRole: '',
      questions: [],
      answers: [],
      expertiseGroups: [],
      selectedExpertise: [],
      expertiseLoaded: false,
      careerHighlights: ['', ''],
      careerHighlightsLoaded: false,
      minCareerHighlights: 2,
      maxCareerHighlights: 4,
      careerHighlightMaxLength: 300,
      uploadData: null,
      replaceFile: null,
      contractStatusOptions: [
        { value: 'PTY', label: 'PTY', caption: 'Operate as Pty Ltd' },
        { value: 'PAYG', label: 'PAYG', caption: 'Screened payroll' }
      ],
      error: false,
      errorMessage: '',
      fadeTimeout: null,
      visibilitySaving: false,
    };
  },
  computed: {
    navSections() {
      const hasValue = this.hasValue;
      const isPositive = (value) => hasValue(value) && parseFloat(value) > 0;
      const salaryValid = isPositive(this.profile.salary_min) || isPositive(this.profile.salary_max);
      const rateValid = (isPositive(this.profile.rate_min) || isPositive(this.profile.rate_max)) && ['hour','day','month'].includes(this.profile.rate_type || '');
      return this.navSectionsBase
        .filter(section => section.id !== 'expertise' || this.expertiseGroups.length > 0)
        .map(section => {
        switch (section.id) {
          case 'visibility':
            return { ...section, complete: true };
          case 'resume':
            return { ...section, complete: !!(this.uploadData && this.uploadData.active) };
          case 'personal':
            return { ...section, complete: hasValue(this.profile.first_name) && hasValue(this.profile.last_name) && hasValue(this.profile.phone) };
          case 'visa':
            return { ...section, complete: hasValue(this.profile.visa_status) && (this.profile.visa_status !== 'Other' || hasValue(this.profile.visa_status_other)) };
          case 'role':
            return { ...section, complete: !!this.selectedIndustry && !!this.selectedRole };
          case 'expertise':
            return { ...section, complete: this.selectedExpertiseIds.length > 0 };
          case 'highlights':
            return { ...section, complete: this.careerHighlights.some(highlight => hasValue(highlight)) };
          case 'remuneration':
            return { ...section, complete: salaryValid || rateValid };
          case 'photo':
            return { ...section, complete: !!this.profilePicturePreview };
          case 'summary':
            return { ...section, complete: hasValue(this.profile.ideal_role) };
          default:
            return section;
        }
      });
    },
    // Selections from another role category stay in memory (so switching back keeps them) but only visible ones count
    selectedExpertiseIds() {
      const visibleIds = new Set(this.expertiseGroups.flatMap(group => group.options.map(option => option.id)));
      return this.selectedExpertise.filter(id => visibleIds.has(id));
    },
    showContractEngagementType() {
      return this.hasValue(this.profile.rate_min) || this.hasValue(this.profile.rate_max);
    },
    hasRoleCategories() {
      return this.roles.some((role) => role.category_name);
    },
    groupedRoles() {
      const groups = [];
      const groupMap = new Map();

      this.roles.forEach((role) => {
        const categoryName = role.category_name || 'Other roles';

        if (!groupMap.has(categoryName)) {
          const group = { name: categoryName, roles: [] };
          groupMap.set(categoryName, group);
          groups.push(group);
        }

        groupMap.get(categoryName).roles.push(role);
      });

      return groups;
    }
  },
  methods: {
    scrollToAnchor(anchor) {
      if (!anchor || typeof window === 'undefined') return;
      const target = document.querySelector(anchor);
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    hasValue(value) {
      return value !== undefined && value !== null && String(value).trim() !== '';
    },
    async getContractorProfile() {
        this.toggleLoading(true);

        try {
            let response = await fetch(apiEndpoint + "/marketplace.php?getContractorAccount", {
                headers: {
                'Authorization': 'Bearer ' +  localStorage.getItem('contractorToken'),
            },
        })

        this.profile = await response.json();
        this.setAvailability(this.profile.availability)
        await this.loadRemunerationRanges()
        if (this.profile.profile_picture_s3_key || this.profile.photo_hash) {
          this.getImage(this.profile.photo_hash, this.profile.profile_picture_s3_key);
        }
        // If the availability is higher than 1 we need to add the custom date to the array
        if (this.availabilityOptions.length >= 6) {
          this.availabilityOptions.pop();
        }

        if (this.selectedAvailability > 1) {
          let customDate = new Date(this.profile.availability * 1000)
          this.availabilityOptions.push({
            "value": this.selectedAvailability,
            "label": 'From: ' + customDate.toLocaleDateString()
          })
        }

        // Set up visa status. If not in array set to other
        if (this.profile.visa_status !== 'Citizen' && this.profile.visa_status !== 'Permanent Resident' && this.profile.visa_status !== 'Visa with full working rights' && this.profile.visa_status !== 'Visa that requires sponsorship' && this.profile.visa_status !== 'Working Holiday Visa' && this.profile.visa_status !== 'I’m not a citizen and don’t hold a visa') {
            this.profile.visa_status_other = this.profile.visa_status
            this.profile.visa_status = "Other"
        }

        // Preset dropdown values
        this.selectedIndustry = this.profile.industryId
        this.selectedRole = this.profile.roleId

        await this.fetchRoles()
        await this.getContractorResponses()
        await this.fetchQuestions(true)
        await this.getContractorExpertise()
        await this.loadCareerHighlights()
        await this.getUploads()
        this.toggleLoading(false);
      } catch (error) {
          this.toggleLoading(false);
      }
    },
    downloadResume() {
        this.toggleLoading(true);
        contractorService.getResume()
        .then(response => {
            const contentType = response.headers.get('content-type');
            let extension;
            switch(contentType) {
                case 'application/pdf':
                    extension = 'pdf'
                    break;
                case 'application/msword':
                    extension = 'doc'
                    break;
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    extension = 'docx'
                    break
            }

            if (response.status === 200 && (contentType === 'application/pdf' ||
                contentType === 'application/msword' ||
                contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {

                const outputFileName = `${this.profile.profile_hash}.${this.profile.file_extension}`;
                const myBlob = response.data;  // Blob data from the response
                const url = window.URL.createObjectURL(myBlob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', outputFileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link); // Clean up
                window.URL.revokeObjectURL(url); // Revoke the URL after use
            } else {
                this.$root.notify('Your resume could not be retrieved at this time. Please try again later..', 'error');
            }
        })
        .catch((error) => {
            this.$root.notify('Something went wrong while fetching the resume for the selected candidate, please try again', 'error');
        })
        .finally(() => {
            this.toggleLoading(false);
        })
    },
    replaceResumeClick() {
        this.$refs.fileInput.click();
    },
    replaceNotificationConfirm() {
        this.toggleLoading(true);

        const formData = new FormData();
        formData.append('replaceFile', this.replaceFile);

        contractorService.updateResume(formData)
        .then(response => {
            if (response.data.type == 'error') {
              this.$root.notify(response.data.message, 'error');
            } else {
              this.uploadData = response.data
            }
        })
        .catch((error) => {
            this.$root.notify('Something went wrong while updating your account settings, please try again', 'error');
        })
        .finally(() => {
            this.toggleLoading(false);
        })
    },
    replaceNotificationCancel() {
        this.replaceFile = null
    },
    handleResumeReplacementUpload() {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        const extension = file.name.split('.').pop().toLowerCase();

        if (ALLOWED_RESUME_MIME_TYPES.includes(file.type) || ALLOWED_RESUME_EXTENSIONS.includes(extension)) {
            this.replaceFile = file
        } else {
            this.$root.notify('Invalid file selected. Only PDF, DOC and DOCX files are allowed.', 'error');
        }
    },
    // Ranges come from their own endpoint so the main profile load never depends on the new columns.
    // Profiles without a range (or if the request fails) show the single salary / rate in both boxes.
    async loadRemunerationRanges() {
      let ranges = null;
      try {
        ranges = (await contractorService.getRemuneration()).data;
      } catch (error) {
        ranges = null;
      }

      const amountOrEmpty = (value) => (value !== null && value !== undefined && parseFloat(value) > 0 ? value : '');
      const pick = (min, max, single) => (amountOrEmpty(min) !== '' || amountOrEmpty(max) !== '')
        ? [amountOrEmpty(min), amountOrEmpty(max)]
        : [amountOrEmpty(single), amountOrEmpty(single)];

      [this.profile.salary_min, this.profile.salary_max] = pick(ranges?.salary_min, ranges?.salary_max, this.profile.salary);
      [this.profile.rate_min, this.profile.rate_max] = pick(ranges?.rate_min, ranges?.rate_max, this.profile.rate);
    },
    async getUploads() {
      await contractorService.getUploads()
      .then(response => {
          this.uploadData = response.data
      })
    },
    setAvailability(v) {
      if (v == null) {
        this.selectedAvailability = 0
      } else {
        this.selectedAvailability = v
      }
    },
    async changeIndustry() {
        this.selectedRole = ''
        await this.fetchRoles()
    },
    async fetchRoles() {
        this.roles = [];

        this.questions = []
        this.answers = []

        // Populate roles
        let roleData = await genericService.getRoles(this.selectedIndustry)
        this.roles = roleData.data

        // Populate quetions
        if (this.selectedRole) {
          let questions = await genericService.getQuestions(this.selectedRole)
        }
    },
    async validateForm() {
        this.error = false;
        this.errorMessage = '';

        let isValid = true;

        const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';
        // Min and max are each optional; anything entered must be a positive amount and min <= max
        const readRange = (minValue, maxValue) => {
            const parse = (value) => hasValue(value) ? parseFloat(value) : null;
            const isValidAmount = (value) => value !== null && !isNaN(value) && value > 0;
            const min = parse(minValue);
            const max = parse(maxValue);
            return {
                invalid: (hasValue(minValue) && !isValidAmount(min)) || (hasValue(maxValue) && !isValidAmount(max)),
                reversed: isValidAmount(min) && isValidAmount(max) && min > max,
                provided: isValidAmount(min) || isValidAmount(max),
            };
        };
        const salaryRange = readRange(this.profile.salary_min, this.profile.salary_max);
        const rateRange = readRange(this.profile.rate_min, this.profile.rate_max);
        const hasSalary = salaryRange.provided;
        const hasRate = rateRange.provided;

        if (salaryRange.invalid) {
            this.errorMessage = 'Enter a valid salary amount.';
            isValid = false;
        } else if (salaryRange.reversed) {
            this.errorMessage = 'The minimum salary cannot be higher than the maximum.';
            isValid = false;
        }

        if (rateRange.invalid) {
            this.errorMessage = 'Enter a valid rate amount.';
            isValid = false;
        } else if (rateRange.reversed) {
            this.errorMessage = 'The minimum rate cannot be higher than the maximum.';
            isValid = false;
        }

        if (!hasSalary && !hasRate) {
            this.errorMessage = 'Please provide a salary or a rate.';
            isValid = false;
        }

        if (hasRate) {
            if (!['hour', 'day', 'month'].includes(this.profile.rate_type)) {
                this.errorMessage = 'Please select how your rate is billed.';
                isValid = false;
            }
            if (!this.profile.contract_type) {
                this.errorMessage = 'Please select your contract engagement type.';
                isValid = false;
            }
        } else {
            this.profile.rate_type = '';
            this.profile.contract_type = '';
        }

        if (!this.profile.ideal_role) {
            this.errorMessage = 'Please provide a short description of your preferred role.'
            isValid = false;
        }

        if (!this.selectedAvailability) {
            this.errorMessage = 'Please select your availability.';
            isValid = false;
        }

        this.answers.forEach((answer, index) => {
            if (!answer || answer.trim() === '') {
              this.errorMessage = 'All questions are mandatory.';
              isValid = false;
            }
        });

        if (!this.selectedRole) {
            this.errorMessage = 'Please select a role from the list.';
            isValid = false;
        }

        if (!this.selectedIndustry) {
            this.errorMessage = 'Please select an industry from the list.'
            isValid = false;
        }

        if (!this.profile.visa_status) {
            this.errorMessage = 'A valid visa status is required.';
            isValid = false;
        }

        if (this.profile.visa_status === 'Other' && !this.profile.visa_status_other) {
            this.errorMessage = 'Please specify your visa status details.';
            isValid = false;
        }

        if (!this.profile.address || !this.profile.suburb || !this.profile.state || !this.profile.post_code ||  this.profile.address.length < 5) {
            this.errorMessage = 'Please enter a valid address.';
            isValid = false;
        }

        if (!this.profile.phone) {
            this.errorMessage = 'A valid phone number is required.';
            isValid = false;
        }

        if (!this.profile.first_name || !this.profile.last_name) {
            this.errorMessage = 'Please provide both your first and last name.';
            isValid = false;
        }

        if (!isValid) {
          this.$root.notify(this.errorMessage, 'error');
        }

        return isValid;
        
    },
    async submitUpdate() {
      this.toggleLoading(true);
        let isValid = await this.validateForm()

        if (isValid) {
            const formData = new FormData();
            const payload = {
              first_name: this.profile.first_name,
              last_name: this.profile.last_name,
              phone: this.profile.phone,
              address: this.profile.address,
              suburb: this.profile.suburb,
              state: this.profile.state,
              post_code: this.profile.post_code,
              visa_status: this.profile.visa_status,
              visa_status_other: this.profile.visa_status_other,
              industry: this.selectedIndustry,
              role: this.selectedRole,
              availability: this.selectedAvailability,
              salary_min: this.profile.salary_min ?? '',
              salary_max: this.profile.salary_max ?? '',
              rate_min: this.profile.rate_min ?? '',
              rate_max: this.profile.rate_max ?? '',
              rate_type: this.profile.rate_type || '',
              contract_type: this.profile.contract_type || '',
              ideal_role: this.profile.ideal_role,
              questions: JSON.stringify(this.questions),
              answers: JSON.stringify(this.answers)
            };

            // The server keeps only options valid for the selected role. Skip when selections failed to load so they aren't wiped
            if (this.expertiseLoaded) {
              payload.expertise = JSON.stringify(this.selectedExpertise);
            }
            // Same for highlights: never overwrite saved highlights we couldn't load
            if (this.careerHighlightsLoaded) {
              payload.career_highlights = JSON.stringify(
                this.careerHighlights.map(highlight => highlight.trim()).filter(highlight => highlight !== '')
              );
            }

            Object.keys(payload).forEach(key => {
              formData.append(key, payload[key] === undefined || payload[key] === null ? '' : payload[key]);
            });

            if (this.formData.profilePicture) {
              formData.append('profilePicture', this.formData.profilePicture);
            }
            if (this.formData.croppedImage) {
              formData.append('croppedImage', this.formData.croppedImage);
            }
            if (this.formData.removeProfilePicture) {
              formData.append('removeProfilePicture', '1');
            }

            contractorService.updateContractorProfile(formData)
            .then(response => {
                this.jobs = response.data;
                this.$root.notify('Your profile has been updated.', 'success');
            })
            .catch((error) => {
              this.$root.notify('Something went wrong while updating your profile information, please try again', 'error');
            })
            .finally(() => {
              this.toggleLoading(false);
            })
        } else {
            this.toggleLoading(false);
        }
    },
    async changeProfileVisibility(value) {
      const previous = this.profile.profile_visibility || 'public';
      if (value === previous || this.visibilitySaving) {
        return;
      }

      this.profile.profile_visibility = value;
      this.visibilitySaving = true;

      try {
        await contractorService.updateContractorSettings({ setting: 'profileVisibility', profileVisibility: value });
        this.$root.notify(value === 'private' ? 'Your profile is now private.' : 'Your profile is now public.', 'success');
      } catch (error) {
        this.profile.profile_visibility = previous;
        this.$root.notify('We couldn\'t update your profile visibility, please try again.', 'error');
      } finally {
        this.visibilitySaving = false;
      }
    },
    availabilityChange(event) {
      const value = Number(event.target.value);
      this.selectedAvailability = value;
      if (value === 999) {
        this.calendarOpen = true;
      } else {
        this.calendarOpen = false;
      }
    },
    updateCalendarDate(newDate) {
      let newSelectedDate = (newDate.getTime() / 1000)
      this.calendarOpen = false

      if (this.availabilityOptions.length >= 6) {
        this.availabilityOptions.pop();
      }

      this.availabilityOptions.push({
        "value": newSelectedDate,
        "label": "From " + newDate.toLocaleDateString()
      })

      this.selectedAvailability = newSelectedDate
    },
    closeModal() {
      this.showModal = false;
    },
    saveImage(imageData) {
      if (!imageData) {
        this.closeModal();
        return;
      }
      this.formData.profilePicture = this.dataURLtoFile(
        imageData,
        (this.profilePictureName || 'profile-photo') + '.webp'
      );
      this.formData.croppedImage = imageData;
      this.logFileDebug(this.formData.profilePicture, 'Cropped file');
      this.profilePicturePreview = imageData;
      this.showModal = false;
    },
    dataURLtoFile(dataUrl, filename) {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },
    logFileDebug(file, label) {
      if (!file) { return; }
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      // eslint-disable-next-line no-console
      console.log(`[Profile Photo Debug] ${label}: name=${file.name || 'unnamed'}, size=${sizeMB}MB, type=${file.type}`);
    },
    showError(message) {
      this.error = true;
      this.errorMessage = message;

      if (this.fadeTimeout) {
        clearTimeout(this.fadeTimeout);
      }

      this.fadeTimeout = setTimeout(() => {
        this.error = false;
      }, 5000);
    },
    async handleProfilePictureUpload(event) {
      const file = event.target.files[0];

      if (!file) {
        return;
      }

      const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB limit

      if (!file.type.startsWith('image/')) {
        this.showError('Please upload a valid image file.');
        event.target.value = '';
        return;
      }

      if (file.size > MAX_UPLOAD_BYTES) {
        this.showError('This file is larger than 25 MB. Please choose a smaller image.');
        event.target.value = '';
        return;
      }

      if (this.activePreviewUrl) {
        URL.revokeObjectURL(this.activePreviewUrl);
        this.activePreviewUrl = null;
      }

      this.profilePictureName = file.name;
      let imageToUse = file;
      this.logFileDebug(file, 'Original file');

      try {
        this.isCompressingPhoto = true;
        const options = {
          maxSizeMB: 1.25,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
          initialQuality: 0.85
        };
        imageToUse = await imageCompression(file, options);
        this.logFileDebug(imageToUse, 'Optimized file');
      } catch (compressionError) {
        // eslint-disable-next-line no-console
        console.warn('Photo compression failed. Using original file.', compressionError);
      } finally {
        this.isCompressingPhoto = false;
      }

      this.formData.profilePicture = imageToUse;
      this.profilePicturePreview = URL.createObjectURL(imageToUse);
      this.activePreviewUrl = this.profilePicturePreview;
      this.showModal = true;
      event.target.value = '';
    },
    removePhoto() {
      this.formData.profilePicture = null;
      this.formData.croppedImage = null;
      this.profilePictureName = '';
      this.profilePicturePreview = null;
      this.formData.removeProfilePicture = true;
      if (this.activePreviewUrl) {
        URL.revokeObjectURL(this.activePreviewUrl);
        this.activePreviewUrl = null;
      }
    },
    reCropPhoto() {
      if (this.formData.profilePicture) {
        this.profilePicturePreview = URL.createObjectURL(this.formData.profilePicture);
        this.activePreviewUrl = this.profilePicturePreview;
        this.showModal = true;
        return;
      }

      if (this.profilePicturePreview) {
        this.showModal = true;
      }
    },
    getImage(hash, s3Key) {
      const imageSource = genericService.getProfilePictureLink(hash, s3Key, true);
      if (imageSource) {
        this.profilePicturePreview = imageSource;
        this.hasPhotoLoaded = true;
        this.formData.removeProfilePicture = false;
      }
    },
    async fetchQuestions(preserveExisting = false) {
      const previousAnswers = preserveExisting ? [...this.answers] : [];
      this.questions = [];
      this.answers = [];

      if (this.selectedRole) {
        try {
          const response = await genericService.getQuestions(this.selectedRole);
          this.questions = response.data || [];

          this.answers = this.questions.map((_, idx) => previousAnswers[idx] || '');
          if (!Array.isArray(this.answers)) {
            this.answers = [this.answers];
          }
        } catch (error) {
          console.error('Error fetching questions:', error);
        }
      }
    },
    async fetchExpertiseOptions() {
      if (!this.selectedRole) {
        this.expertiseGroups = [];
        return;
      }

      try {
        const response = await genericService.getExpertiseOptions(this.selectedRole);
        this.expertiseGroups = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.expertiseGroups = [];
        console.error('Error fetching expertise options:', error);
      }
    },
    async getContractorExpertise() {
      try {
        const response = await contractorService.getExpertise();
        this.selectedExpertise = Array.isArray(response.data) ? response.data.map(Number) : [];
        this.expertiseLoaded = true;
      } catch (error) {
        this.selectedExpertise = [];
        this.expertiseLoaded = false;
      }
    },
    async loadCareerHighlights() {
      try {
        const response = await contractorService.getCareerHighlights();
        this.careerHighlights = this.parseCareerHighlights(response.data);
        this.careerHighlightsLoaded = true;
      } catch (error) {
        this.careerHighlights = this.parseCareerHighlights([]);
        this.careerHighlightsLoaded = false;
      }
    },
    parseCareerHighlights(value) {
      let highlights = [];
      try {
        highlights = Array.isArray(value) ? value : JSON.parse(value || '[]');
      } catch (error) {
        highlights = [];
      }
      highlights = (Array.isArray(highlights) ? highlights : [])
        .filter(highlight => typeof highlight === 'string')
        .slice(0, this.maxCareerHighlights);

      while (highlights.length < this.minCareerHighlights) {
        highlights.push('');
      }
      return highlights;
    },
    addCareerHighlight() {
      if (this.careerHighlights.length < this.maxCareerHighlights) {
        this.careerHighlights.push('');
      }
    },
    removeCareerHighlight(index) {
      this.careerHighlights.splice(index, 1);
    },
    async getContractorResponses() {
      try {
        let response = await fetch(apiEndpoint + "/marketplace.php?getContractorResponses", {
          headers: {
            'Authorization': 'Bearer ' +  localStorage.getItem('contractorToken'),
          },
        })
        let answersData = await response.json();
        this.answers = answersData.map(answer => answer.response);
      } catch (error) {
      }
      },
  },
  watch: {
    'profile.visa_status': function(newValue) {
      if (newValue !== 'Other') {
        this.profile.visa_status_other = '';
      }
    },
    selectedRole(newValue) {
      this.fetchExpertiseOptions();
      if (!newValue) {
        this.questions = [];
        this.answers = [];
        return;
      }
      this.fetchQuestions(true);
    }
  },
  created() {
    this.getContractorProfile();
    (async () => this.industries = (await genericService.getIndustries()).data)()
  },
  beforeUnmount() {
    if (this.activePreviewUrl) {
      URL.revokeObjectURL(this.activePreviewUrl);
    }
  }
}
</script>
